import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart2, Filter, Loader2, Wallet, X } from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useAllClaims, useMarkClaimPaid } from "../hooks";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import type { ClaimStatus, ClaimType, ExpenseClaimSummary } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) { return `KES ${n.toLocaleString()}`; }

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
}

function typeLabel(t: ClaimType) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAGE_SIZE = 15;

const ALL_STATUSES: ClaimStatus[] = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "CANCELLED", "PAID"];
const ALL_TYPES: ClaimType[] = ["TRAVEL", "MEAL", "MEDICAL", "ACCOMMODATION", "OFFICE_SUPPLIES", "TRAINING", "OTHER"];

// ─── inline pay button (owns its own mutation state per row) ──────────────────

function MarkPaidButton({ claim }: { claim: ExpenseClaimSummary }) {
  const markPaid = useMarkClaimPaid();
  const confirm = useConfirm();
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        if (!(await confirm(`Mark ${claim.claimNo} as paid (KES ${claim.totalAmount.toLocaleString()})?`))) return;
        await markPaid.mutateAsync(claim.id);
      }}
      disabled={markPaid.isPending}
      className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
    >
      <Wallet className="h-3 w-3" />
      {markPaid.isPending ? "…" : "Pay"}
    </button>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function AdminClaimsPage() {
  const navigate  = useNavigate();
  const { hasRole, hasAnyRole, hasPermission } = useAuth();

  const isAdmin    = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  // HR_CLAIM_VIEW_ALL is the canonical permission for all-employees list (V87)
  const canViewAll = isAdmin || hasPermission("HR_CLAIM_VIEW_ALL");
  const canMarkPaid = isAdmin || hasPermission("HR_CLAIM_APPROVE") || hasPermission("FINANCE_CLAIM_APPROVE");

  if (!canViewAll) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-8 text-center">
        <p className="font-bold text-red-700">Access denied.</p>
        <p className="mt-1 text-sm text-red-500">You do not have permission to view all claims.</p>
        <Link to="/claims" className="mt-3 inline-block text-xs font-bold text-red-600 hover:underline">
          ← Back to my claims
        </Link>
      </div>
    );
  }

  const [page, setPage]                 = useState(0);
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | "">("");
  const [typeFilter, setTypeFilter]     = useState<ClaimType | "">("");
  const [search, setSearch]             = useState("");

  const hasFilters = statusFilter || typeFilter || search;

  const { data, isLoading } = useAllClaims({
    page,
    size: PAGE_SIZE,
    ...(statusFilter && { status: statusFilter }),
    ...(typeFilter   && { claimType: typeFilter }),
    ...(search       && { search }),
  });

  const claims     = data?.content ?? [];
  const total      = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const totalAmt = claims.reduce((s, c) => s + (c.totalAmount ?? 0), 0);

  function clearFilters() {
    setStatusFilter(""); setTypeFilter(""); setSearch(""); setPage(0);
  }

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">All Claims</h1>
          {total > 0 && (
            <p className="mt-0.5 text-xs text-slate-500">{total} total · {fmt(totalAmt)}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/claims" className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
            ← Dashboard
          </Link>
          <Link
            to="/claims/reports"
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-100"
          >
            <BarChart2 className="h-4 w-4" /> Reports
          </Link>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
        <Filter className="h-4 w-4 shrink-0 self-center text-slate-400" />
        <input
          type="text"
          placeholder="Search employee or claim no…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm"
        />
        <select
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as ClaimStatus | ""); setPage(0); }}
        >
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select
          className="rounded-xl border bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value as ClaimType | ""); setPage(0); }}
        >
          <option value="">All types</option>
          {ALL_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {/* ── Table ── */}
      {isLoading ? (
        <div className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-8 text-slate-400 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading claims…
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3">Claim</th>
                <th className="px-3 py-3">Employee</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Amount</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Submitted</th>
                <th className="px-3 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {claims.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-slate-400">
                    No claims found.
                  </td>
                </tr>
              ) : claims.map((c) => (
                <tr key={c.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-xs font-black text-emerald-700">
                        {c.claimType.charAt(0)}
                      </div>
                      <span className="text-xs font-bold text-slate-500">{c.claimNo}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-semibold text-slate-700">{c.employeeName}</td>
                  <td className="px-3 py-3 text-slate-600">{typeLabel(c.claimType)}</td>
                  <td className="px-3 py-3 font-black text-slate-800">{fmt(c.totalAmount ?? 0)}</td>
                  <td className="px-3 py-3"><ClaimStatusBadge status={c.status} /></td>
                  <td className="px-3 py-3 text-xs text-slate-400">{fmtDate(c.submittedAt)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canMarkPaid && c.status === "APPROVED" && (
                        <MarkPaidButton claim={c} />
                      )}
                      <button
                        onClick={() => navigate(`/claims/${c.id}`)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100"
                      >
                        View →
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t bg-slate-50 px-5 py-3">
            <span className="text-xs text-slate-400">{total} claim{total !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-xs font-bold text-slate-500">{page + 1} / {totalPages || 1}</span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
